import React, { useMemo } from 'react';
import { Box, Divider, Grid, Tooltip, Typography } from '@mui/material';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { DashboardMetricItem } from '../../api/dashboard';
import { DashboardMetricSection, MetricUpdateButton } from './';
import MetricCounter from './MetricCounter';
import { AssignStatus, NURSE_TYPE_THEME, ResignedStatus, SeniorTalkRoomStatus, TalkStatus, TestAccountStatus } from '../../types/nurse';
import { useNavigate } from 'react-router-dom';
import { formatDateToISO } from '../../utils/dateUtils';

// 登録日関連の指標のデータ型
export interface RegistrationMetrics {
  today: number;
  yesterday: number;
  pastWeek: number;
  currentMonth: number;
  previousMonth: number;
}

interface NurseStatusSectionProps {
  metricData?: DashboardMetricItem;
  isLoading: boolean;
  onUpdate?: (metricKey: string) => Promise<void>;
}

/**
 * 看護師ステータスを表示するセクション
 */
const NurseStatusSection: React.FC<NurseStatusSectionProps> = ({ metricData, isLoading, onUpdate }) => {
  const navigate = useNavigate();
  // 看護師一覧へのリンク処理
  const handleTalkingNurseClick = () => {
    navigate('/nurses', {
      state: {
        roleType: [ 'MANAGER', 'TALK_STAFF' ],
        talkStatus: TalkStatus.IN_TALK,
        resignedStatus: ResignedStatus.ACTIVE_ONLY,
        testAccountStatus: TestAccountStatus.NORMAL
      }
    });
  };
  // 待機中の看護師一覧へのリンク処理
  const handleWaitingNurseClick = () => {
    navigate('/nurses', {
      state: {
        roleType: [ 'MANAGER', 'TALK_STAFF' ],
        talkStatus: TalkStatus.WAITING,
        resignedStatus: ResignedStatus.ACTIVE_ONLY,
        assignStatus: AssignStatus.ASSIGNABLE,
        testAccountStatus: TestAccountStatus.NORMAL
      }
    });
  };
  // 今日登録された看護師を表示するリンク処理
  const handleTodayRegisteredClick = () => {
    // 今日の日付をYYYY-MM-DD形式で取得
    const today = formatDateToISO(new Date());
    navigate('/nurses', {
      state: {
        createdAtStart: today,
        createdAtEnd: today,
        resignedStatus: ResignedStatus.INCLUDE_RESIGNED,
        testAccountStatus: TestAccountStatus.NORMAL
      }
    });
  };
  // 過去７日間登録された看護師を表示するリンク処理
  const handlePastWeekRegisteredClick = () => {
    // 7日前の日付を計算
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const startDate = formatDateToISO(sevenDaysAgo);
    navigate('/nurses', {
      state: {
        createdAtStart: startDate,
        resignedStatus: ResignedStatus.INCLUDE_RESIGNED,
        testAccountStatus: TestAccountStatus.NORMAL
      }
    });
  };
  // 今月登録された看護師を表示するリンク処理
  const handleThisMonthRegisteredClick = () => {
    // 今月の1日を計算
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const startDate = formatDateToISO(firstDayOfMonth);
    navigate('/nurses', {
      state: {
        createdAtStart: startDate,
        resignedStatus: ResignedStatus.INCLUDE_RESIGNED,
        testAccountStatus: TestAccountStatus.NORMAL
      }
    });
  };
  // 承認待ちの研修看護師を表示するリンク処理
  const handleInReviewNurseClick = () => {
    navigate('/nurses', {
      state: {
        roleType: [ 'STAFF' ],
        workshopStatus: [ 'IN_REVIEW' ],
        isTrainingMode: true,
        resignedStatus: ResignedStatus.ACTIVE_ONLY,
        testAccountStatus: TestAccountStatus.NORMAL
      }
    });
  };
  // 研修停滞中の研修看護師を表示するリンク処理
  const handleExerciseInactiveNurseClick = () => {
    navigate('/nurses', {
      state: {
        roleType: [ 'STAFF' ],
        workshopStatus: [ 'DOING' ],
        isTrainingMode: true,
        resignedStatus: ResignedStatus.ACTIVE_ONLY,
        testAccountStatus: TestAccountStatus.NORMAL
      }
    });
  };
  // 研修未着手の研修看護師を表示するリンク処理
  const handleDelayedRestartNurseClick = () => {
    navigate('/nurses', {
      state: {
        roleType: [ 'STAFF' ],
        workshopStatus: [ 'READY' ],
        workshopFinishCount: '1',
        workshopFinishCountOperator: 'gte',
        isTrainingMode: true,
        resignedStatus: ResignedStatus.ACTIVE_ONLY,
        testAccountStatus: TestAccountStatus.NORMAL
      }
    });
  };
  // サポートなし看護師を表示するリンク処理
  const handleNoSupportNurseClick = () => {
    navigate('/nurses', {
      state: {
        roleType: [ 'STAFF', 'TALK_STAFF' ],
        seniorTalkRoomStatus: SeniorTalkRoomStatus.NO_RELATIONSHIP,
        resignedStatus: ResignedStatus.ACTIVE_ONLY,
        testAccountStatus: TestAccountStatus.NORMAL
      }
    });
  };
  // 登録日関連のデータをAPIレスポンスから取得
  const registrationMetrics = useMemo(() => {
    if (!metricData) {
      return {
        today: 0,
        yesterday: 0,
        pastWeek: 0,
        currentMonth: 0,
        previousMonth: 0
      };
    }
    const {
      nurse__registered_today = 0,
      nurse__registered_yesterday = 0,
      nurse__registered_last_7days = 0,
      nurse__registered_this_month = 0,
      nurse__registered_last_month = 0
    } = metricData.current_values;
    // APIから取得した値が文字列の場合があるので数値に変換する
    return {
      today: Number(nurse__registered_today),
      yesterday: Number(nurse__registered_yesterday),
      pastWeek: Number(nurse__registered_last_7days),
      currentMonth: Number(nurse__registered_this_month),
      previousMonth: Number(nurse__registered_last_month)
    };
  }, [ metricData ]);
  // メトリクスデータからセクションコンテンツを生成する関数
  const renderNurseStatusContent = (data: DashboardMetricItem) => {
    const {
      nurse__talking_count = 0,
      nurse__waiting_count = 0,
      workshop_nurse__total_count = 0,
      workshop_nurse__in_review_count = 0,
      workshop_nurse__delayed_restart_count = 0,
      workshop_nurse__exercise_inactive_count = 0
    } = data.current_values;
    return (
      <Grid
        container
        spacing={2}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: { xs: 'center', sm: 'flex-start' }
        }}
      >
        {/* 基本セクション */}
        <Grid item xs={12} container spacing={2}>
          {/* 総看護師数はセクションタイトルの横に表示するため削除 */}

          <Grid item xs={6} sm={4} md={3} lg={2} xl={2}>
            <MetricCounter
              label="対話中"
              value={nurse__talking_count}
              color="success.main"
              tooltip={"条件\n・'マネージャー', '対話看護師' のみ\n・現在対話中 (対話ステータスが'対話中')\n・有効ユーザーのみ"}
              onClick={handleTalkingNurseClick}
            />
          </Grid>

          <Grid item xs={6} sm={4} md={3} lg={2} xl={2}>
            <MetricCounter
              label="待機中"
              value={nurse__waiting_count}
              color="error.main"
              tooltip={"条件\n・申込み受付中\n・'マネージャー', '対話看護師' のみ\n・過去の対話がない。もしくは、対話ステータスが、'対話終了', か 'キャンセル'しかない状態"}
              onClick={handleWaitingNurseClick}
            />
          </Grid>

          {/* 今月の対話人数 */}
          <Grid item xs={6} sm={4} md={3} lg={2} xl={2}>
            <Box sx={{ position: 'relative', height: '100%' }}>
              <MetricCounter
                label="今月の対話"
                value={metricData?.current_values.nurse__talked_this_month || 0}
                color="info.main"
                tooltip="今月対話をした看護師の数"
                disableHoverEffect={true}
              />
              <Typography
                color="text.secondary"
                variant="caption"
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  right: 8
                }}
              >
                有料プラン: <span style={{ fontSize: '1.2rem' }}>{metricData?.current_values.nurse__paid_talked_this_month || 0}</span>
              </Typography>
            </Box>
          </Grid>

          {/* 先月の対話人数 */}
          <Grid item xs={6} sm={4} md={3} lg={2} xl={2}>
            <Box sx={{ position: 'relative', height: '100%' }}>
              <MetricCounter
                label="先月の対話"
                value={metricData?.current_values.nurse__talked_last_month || 0}
                color="info.light"
                tooltip="先月対話をした看護師の数"
                disableHoverEffect={true}
              />
              <Typography
                color="text.secondary"
                variant="caption"
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  right: 8
                }}
              >
                有料プラン: <span style={{ fontSize: '1.2rem' }}>{metricData?.current_values.nurse__paid_talked_last_month || 0}</span>
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* 区切り線 */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }}/>
        </Grid>

        {/* 登録数に関するメトリクス */}
        <Grid item xs={12} container spacing={2}>
          {/* 前日比の計算とトレンドアイコンの表示 */}
          {(() => {
            const todayVsYesterday = registrationMetrics.today - registrationMetrics.yesterday;
            // 前日比のパーセント計算を追加
            let todayPercentChange: number | null = 0;
            if (registrationMetrics.yesterday > 0) {
              todayPercentChange = Math.round((todayVsYesterday / registrationMetrics.yesterday) * 100);
              // 極端なパーセンテージ（999%以上）は表示しない
              if (todayPercentChange > 999) {
                todayPercentChange = null;
              }
            } else if (todayVsYesterday > 0) {
              // 昨日が0の場合は「新規」としてパーセント表示しない
              todayPercentChange = null;
            }
            const todayTrend = todayVsYesterday >= 0 ? (
              <TrendingUpIcon fontSize="small" color="success"/>
            ) : (
              <TrendingDownIcon fontSize="small" color="error"/>
            );
            // 今月と先月の比較
            const monthlyDiff = registrationMetrics.currentMonth - registrationMetrics.previousMonth;
            // パーセント変化の計算（増加および減少）
            let monthPercentChange: number | null = 0;
            if (registrationMetrics.previousMonth > 0) {
              monthPercentChange = Math.round((monthlyDiff / registrationMetrics.previousMonth) * 100);
              // 極端なパーセンテージ（999%以上）は表示しない
              if (monthPercentChange > 999) {
                monthPercentChange = null;
              }
            } else if (monthlyDiff > 0) {
              // 先月が0の場合は「新規」としてパーセント表示しない
              monthPercentChange = null;
            }
            const monthTrend = monthlyDiff >= 0 ? (
              <TrendingUpIcon fontSize="small" color="success"/>
            ) : (
              <TrendingDownIcon fontSize="small" color="error"/>
            );
            if (isLoading) {
              return (
                <Grid item xs={12}>
                  <Box sx={{ opacity: 0.7 }}>
                    <Typography variant="body2" color="text.secondary">データ読み込み中...</Typography>
                  </Box>
                </Grid>
              );
            }
            return (
              <>
                {/* 今日と昨日の登録数 */}
                <Grid item xs={6} sm={3} md={3} lg={2} xl={2}>
                  <Box sx={{ position: 'relative', height: '100%' }}>
                    <MetricCounter
                      label="今日の登録"
                      value={registrationMetrics.today}
                      tooltip={
                        todayVsYesterday !== 0
                          ? `前日比: ${todayVsYesterday > 0 ? '+' : ''}${todayVsYesterday} 名 ${
                            todayPercentChange === null
                              ? registrationMetrics.yesterday === 0 ? '(昨日0名)' : ''
                              : `(${Math.abs(todayPercentChange)}%)`
                          }`
                          : '前日比: 変化なし'
                      }
                      onClick={handleTodayRegisteredClick}
                    />
                    <Typography
                      color="text.secondary"
                      variant="caption"
                      sx={{
                        position: 'absolute',
                        bottom: 8,
                        right: 8
                      }}
                    >
                      昨日: <span style={{ fontSize: '1.2rem' }}>{registrationMetrics.yesterday}</span>
                    </Typography>
                  </Box>
                </Grid>

                {/* 過去7日間の登録数 */}
                <Grid item xs={6} sm={3} md={3} lg={2} xl={2}>
                  <Box sx={{ position: 'relative', height: '100%' }}>
                    <MetricCounter
                      label="過去7日間"
                      value={registrationMetrics.pastWeek}
                      tooltip="過去7日間の新規登録看護師数"
                      onClick={handlePastWeekRegisteredClick}
                    />
                    <Typography
                      color="text.secondary"
                      variant="caption"
                      sx={{
                        position: 'absolute',
                        bottom: 8,
                        right: 8
                      }}
                    >
                      1日平均: <span style={{ fontSize: '1.2rem' }}>{Math.round(registrationMetrics.pastWeek / 7)}</span>
                    </Typography>
                  </Box>
                </Grid>

                {/* 今月と先月の登録数（統合） */}
                <Grid item xs={6} sm={3} md={3} lg={2} xl={2}>
                  <Box sx={{ position: 'relative', height: '100%' }}>
                    <MetricCounter
                      label="今月の登録"
                      value={registrationMetrics.currentMonth}
                      tooltip={
                        monthlyDiff !== 0
                          ? `先月比: ${monthlyDiff > 0 ? '+' : ''}${monthlyDiff} 名 ${
                            monthPercentChange === null
                              ? registrationMetrics.previousMonth === 0 ? '(先月0名)' : ''
                              : `(${monthPercentChange}%)`
                          }`
                          : '先月比: 変化なし'
                      }
                      onClick={handleThisMonthRegisteredClick}
                    />
                    <Typography
                      color="text.secondary"
                      variant="caption"
                      sx={{
                        position: 'absolute',
                        bottom: 8,
                        right: 8
                      }}
                    >
                      先月: <span style={{ fontSize: '1.2rem' }}>{registrationMetrics.previousMonth}</span>
                    </Typography>
                  </Box>
                </Grid>
              </>
            );
          })()}
        </Grid>

        {/* 区切り線 */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }}/>
        </Grid>

        {/* 研修看護師セクション - 上位のレイヤーを基本セクション（対話中）と同じ構造に */}
        <Grid item xs={12} container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 1,
              mt: 1,
            }}>
              {/* SubdirectoryArrowRightアイコンとラベルのコンテナ */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
              }}>
                {/* 左端に配置 */}
                <SubdirectoryArrowRightIcon
                  sx={{
                    color: NURSE_TYPE_THEME.TRAINING.DARK,
                    fontSize: 20,
                    mr: 1,
                    opacity: 0.9
                  }}
                />
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: NURSE_TYPE_THEME.TRAINING.DARK,
                    fontWeight: 'medium',
                    borderLeft: `3px solid ${NURSE_TYPE_THEME.TRAINING.DARK}`,
                    pl: 1.5,
                    py: 0.3,
                    opacity: 0.95
                  }}
                >
                  研修看護師
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{
                    ml: 2,
                    color: NURSE_TYPE_THEME.TRAINING.DARK,
                    fontWeight: 'medium',
                  }}
                >
                  {workshop_nurse__total_count || 0}名
                </Typography>
                <Tooltip
                  title={
                    <div style={{ textAlign: 'left', fontSize: '0.875rem' }}>
                      <div>・研修看護師のみ</div>
                      <div>・退会していない</div>
                    </div>
                  }
                  arrow
                  placement="top"
                >
                  <Box
                    component="span"
                    sx={{
                      ml: 0.5,
                      fontSize: '0.6rem',
                      color: 'text.secondary',
                      cursor: 'help',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      border: '1px solid',
                      borderColor: 'text.disabled',
                    }}
                  >
                    i
                  </Box>
                </Tooltip>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* メトリックを配置 - 基本セクションと同じGrid構造にする */}
        <Grid item xs={12} container spacing={2}>
          <Grid item xs={6} sm={4} md={3} lg={2} xl={2}>
            <MetricCounter
              label="サポートなし"
              value={metricData?.current_values.nurse__without_support_count || 0}
              color="error.dark"
              tooltip={"条件\n・研修看護師、対話看護師\n・退会していない\n・オーナーとのサポート関係はカウントしない"}
              onClick={handleNoSupportNurseClick}
            />
          </Grid>

          <Grid item xs={6} sm={4} md={3} lg={2} xl={2}>
            <MetricCounter
              label="承認待ち"
              value={workshop_nurse__in_review_count}
              color="warning.main"
              tooltip="承認待ちの看護師数"
              onClick={handleInReviewNurseClick}
            />
          </Grid>

          <Grid item xs={6} sm={4} md={3} lg={2} xl={2}>
            <MetricCounter
              label="研修停滞中"
              value={workshop_nurse__exercise_inactive_count}
              color="secondary.main"
              tooltip="[演習中]ステータス で '3日以上' 書き込みが無い"
              onClick={handleExerciseInactiveNurseClick}
            />
          </Grid>

          <Grid item xs={6} sm={4} md={3} lg={2} xl={2}>
            <MetricCounter
              label="研修未着手"
              value={workshop_nurse__delayed_restart_count}
              color="warning.dark"
              tooltip="１回以上研修が修了していて、[開始待ち] が 7日以上経過している"
              onClick={handleDelayedRestartNurseClick}
            />
          </Grid>
        </Grid>
      </Grid>
    );
  };
  // タイトルの横に総看護師数を表示する関数
  const renderNurseTitleCount = () => {
    if (!metricData) return null;
    const totalCount = metricData.current_values.nurse__total_count || 0;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography
          variant="subtitle2"
          sx={{
            ml: 2,
            color: NURSE_TYPE_THEME.NORMAL.DARK,
            fontWeight: 'medium'
          }}
        >
          {totalCount}名
        </Typography>
        <Tooltip
          title={
            <div style={{ textAlign: 'left', fontSize: '0.875rem' }}>
              <div>・オーナー以外の看護師</div>
              <div>・退会していない</div>
            </div>
          }
          arrow
          placement="top"
        >
          <Box
            component="span"
            sx={{
              ml: 0.5,
              fontSize: '0.6rem',
              color: 'text.secondary',
              cursor: 'help',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 14,
              height: 14,
              borderRadius: '50%',
              border: '1px solid',
              borderColor: 'text.disabled',
            }}
          >
            i
          </Box>
        </Tooltip>
      </Box>
    );
  };
  // 更新ボタンを右端に表示するためのラッパー
  return (
    <DashboardMetricSection
      title="看護師"
      description=""
      metricData={metricData}
      isLoading={isLoading}
      renderContent={renderNurseStatusContent}
      themeColor={NURSE_TYPE_THEME.NORMAL.DARK} // 看護師テーマカラー #f50057
      renderTitleCount={renderNurseTitleCount} // 総看護師数を表示
      renderButton={
        onUpdate && (
          <MetricUpdateButton
            metricKey="nurse_status"
            onUpdate={onUpdate}
            disabled={isLoading}
          />
        )
      }
    />
  );
};
export default NurseStatusSection;
